/**
 * UpgradeConfirmDialog - Confirmation modal for one-click upgrades
 * Shows price and plan details before confirming the upgrade
 */

import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Loader2, Crown, Check, AlertCircle } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useSafeMode } from '../../contexts/SafeModeContext';

const UpgradeConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  plan,
  isLoading = false,
}) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  if (!plan) return null;
  
  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent className={cn(colors.bgCard, colors.borderPrimary)}>
        <AlertDialogHeader>
          <AlertDialogTitle className={cn("flex items-center gap-2", colors.textPrimary)}>
            <Crown className="w-5 h-5 text-purple-500" />
            Confirm Upgrade
          </AlertDialogTitle>
          <AlertDialogDescription className={colors.textSecondary}>
            You&apos;re about to upgrade to the {plan.label} plan.
          </AlertDialogDescription>
        </AlertDialogHeader>
        
        <div className="py-4 space-y-4">
          {/* Plan Summary Card */}
          <div className={cn(
            "p-4 rounded-lg border-2",
            "bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20",
            "border-purple-200 dark:border-purple-700"
          )}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-purple-700 dark:text-purple-300">
                {plan.label}
              </h3>
              {plan.price_display && (
                <span className="text-lg font-bold text-purple-600 dark:text-purple-400">
                  {plan.price_display}
                </span>
              )}
            </div>
            
            {plan.features && plan.features.length > 0 && (
              <ul className="space-y-2">
                {plan.features.slice(0, 5).map((feature, index) => (
                  <li key={index} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <Check className="w-4 h-4 text-green-500 shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
            )}
          </div>
          
          {/* Billing Notice */}
          <div className={cn(
            "flex items-start gap-2 p-3 rounded-lg text-sm",
            "bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800"
          )}>
            <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="text-amber-800 dark:text-amber-200">
              <p className="font-medium">Billing Information</p>
              <p className="text-xs mt-1 opacity-90">
                Your payment method on file will be charged immediately. 
                If you&apos;re on an existing subscription, the charge will be prorated.
              </p>
            </div>
          </div>
        </div>
        
        <AlertDialogFooter>
          <AlertDialogCancel 
            disabled={isLoading}
            className={cn("border-gray-300 dark:border-gray-600", colors.textPrimary)}
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              onConfirm();
            }}
            disabled={isLoading}
            className="bg-purple-600 hover:bg-purple-700 text-white"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Processing...
              </>
            ) : (
              <>
                <Crown className="w-4 h-4 mr-2" />
                Confirm Upgrade
              </>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default UpgradeConfirmDialog;
